<template>
    <div class="tw:flex tw:flex-col tw:gap-3">
        <!-- Section label -->
        <div v-if="label || maxSelection > 1" class="tw:flex tw:items-center tw:justify-between">
            <p
                v-if="label"
                class="tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:m-0"
            >
                {{ label }}
            </p>
            <span
                v-if="maxSelection > 1"
                class="tw:text-xs tw:font-medium tw:pl-1"
                :class="limitReached ? 'tw:text-orange-600' : 'tw:text-gray-400'"
            >
                {{ $t('seat_picker.selection_summary', { count: selectionCount, max: maxSelection }) }}
            </span>
        </div>
        <!-- Stage bar — terreno de juego / escenario (matches legacy TerrenoJuegoComponent) -->
        <div
            v-if="groupByRow && seats.length"
            class="tw:bg-gray-950 tw:rounded-lg tw:px-4 tw:py-2.5 tw:text-center tw:text-[11px] tw:font-extrabold tw:uppercase tw:tracking-[0.25em] tw:text-white"
        >
            {{ stageDisplayLabel }}
        </div>
        <!-- Loading skeleton — dot grid mimicking groupByRow seat map -->
        <div
            v-if="loading"
            class="tw:flex tw:flex-col tw:gap-1.5 tw:w-full"
            role="status"
            :aria-label="$t('seat_picker.loading')"
        >
            <div
                v-for="n in skeletonCount"
                :key="n"
                class="tw:flex tw:items-center tw:gap-1 tw:w-full"
            >
                <!-- Row label placeholder -->
                <Skeleton padding="tw:p-0" custom-classes="tw:shadow-none">
                    <template #skeleton>
                        <SkeletonItem custom-class="tw:w-2 tw:h-2 tw:rounded-sm" />
                    </template>
                </Skeleton>
                <!-- Dot placeholders -->
                <div class="tw:flex tw:flex-1 tw:gap-1 tw:justify-between">
                    <Skeleton
                        v-for="c in 19"
                        :key="c"
                        padding="tw:p-0"
                        custom-classes="tw:shadow-none"
                    >
                        <template #skeleton>
                            <SkeletonItem custom-class="tw:w-[18px] tw:h-[18px] tw:rounded-full tw:aspect-square" />
                        </template>
                    </Skeleton>
                </div>
            </div>
        </div>

        <!-- Empty state -->
        <p
            v-else-if="!seats.length"
            class="tw:text-sm tw:text-gray-400 tw:text-center tw:py-4 tw:m-0"
        >
            {{ emptyText || $t('seat_picker.empty') }}
        </p>

        <!-- Seat grid / list -->
        <template v-else>
            <!-- Row-grouped view (dot/circle mode — matches Stitch seat map design) -->
            <div
                v-if="groupByRow"
                class="tw:overflow-x-auto tw:pb-1 tw:w-full"
                role="listbox"
                :aria-multiselectable="maxSelection > 1"
                :aria-label="label"
            >
                <div class="tw:flex tw:w-fit">
                    <!-- Columna de etiquetas de fila — sticky a la izquierda -->
                    <div class="tw:sticky tw:left-0 tw:z-10 tw:bg-white tw:pr-1 tw:flex tw:flex-col tw:gap-1.5 tw:py-1">
                        <span
                            v-for="(rowSeats, rowKey) in seatsByRow"
                            :key="'label-' + rowKey"
                            class="tw:h-[20px] tw:flex tw:items-center tw:text-[9px] tw:font-bold tw:text-gray-900"
                        >
                            {{ rowKey }}
                        </span>
                    </div>
                    <!-- Área de butacas — scroll horizontal -->
                    <div class="tw:flex tw:flex-col tw:gap-1.5 tw:py-1 tw:pl-1">
                        <div
                            v-for="(rowSeats, rowKey) in seatsByRow"
                            :key="rowKey"
                            class="tw:flex tw:items-center tw:gap-1.5"
                        >
                            <span
                                v-for="seat in rowSeats"
                                :key="seat.id"
                            >
                                <!-- Hueco/pasillo: placeholder invisible que preserva la posición -->
                                <div
                                    v-if="isGap(seat)"
                                    class="tw:w-[20px] tw:h-[20px] tw:shrink-0"
                                ></div>
                                <!-- Butaca normal -->
                                <button
                                    v-else
                                    type="button"
                                    role="option"
                                    :aria-selected="isSelected(seat.id)"
                                    :aria-disabled="!canToggle(seat)"
                                    :title="seatTitle(seat)"
                                    :class="seatDotClasses(seat)"
                                    :disabled="!canToggle(seat) && !isSelected(seat.id)"
                                    @click="handleToggle(seat)"
                                >
                                    <span class="tw:text-[9px] tw:font-bold">{{ seat.number }}</span>
                                </button>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Flat list view (default) -->
            <ul
                v-else
                class="tw:list-none tw:m-0 tw:p-0 tw:flex tw:flex-col tw:gap-2"
                role="listbox"
                :aria-multiselectable="maxSelection > 1"
                :aria-label="label"
            >
                <li
                    v-for="seat in seats"
                    :key="seat.id"
                    role="option"
                    :aria-selected="isSelected(seat.id)"
                    :aria-disabled="!seat.available"
                    :class="[
                        'tw:flex tw:items-center tw:justify-between tw:gap-3',
                        'tw:px-4 tw:py-3 tw:rounded-lg tw:border tw:cursor-pointer',
                        'tw:transition-all tw:duration-150 tw:select-none',
                        listItemClasses(seat),
                    ]"
                    @click="handleToggle(seat)"
                >
                    <!-- Left: seat info -->
                    <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
                        <span class="tw:text-sm tw:font-semibold tw:leading-tight">
                            {{ seat.label || `${$t('seat_picker.row')} ${seat.row} · ${$t('seat_picker.seat')} ${seat.number}` }}
                        </span>
                        <span
                            v-if="seat.sectionName"
                            class="tw:text-xs tw:text-gray-400 tw:truncate tw:leading-tight"
                        >
                            {{ seat.sectionName }}
                        </span>
                        <BadgeFront
                            v-if="seat.reducedVisibility"
                            :label="seat.visibilityPercent ? `${$t('seat_picker.reduced_visibility')} ${seat.visibilityPercent}%` : $t('seat_picker.reduced_visibility')"
                            variant="warning"
                        />
                    </div>

                    <!-- Right: status indicators -->
                    <div class="tw:flex tw:items-center tw:gap-2 tw:flex-shrink-0">
                        <BadgeFront
                            v-if="!seat.available"
                            :label="$t('seat_picker.unavailable')"
                            variant="danger"
                            :dot="true"
                        />
                        <i
                            v-else-if="isSelected(seat.id)"
                            class="material-icons tw:text-base tw:leading-none tw:text-blue-600"
                            aria-hidden="true"
                        >check_circle</i>
                        <i
                            v-else-if="limitReached"
                            class="material-icons tw:text-base tw:leading-none tw:text-gray-300"
                            aria-hidden="true"
                        >radio_button_unchecked</i>
                    </div>
                </li>
            </ul>
        </template>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import store from '@/store';
    import { t } from '@/utils/i18n-helper';
    import { useSeatSelection } from '@/composables/front/ticketing/useSeatSelection';
    import { useAdjacencyRules } from '@/composables/front/ticketing/useAdjacencyRules';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import Skeleton from '@/modules/front/ui/skeleton/Skeleton.vue';
    import SkeletonItem from '@/modules/front/ui/skeleton/SkeletonItem.vue';

    // ─── Props ───────────────────────────────────────────────────────────────

    const props = defineProps({
        /** Array of seat objects to render */
        seats: {
            type: Array,
            default: () => [],
        },
        /** Max number of seats the user can select */
        maxSelection: {
            type: Number,
            default: 1,
        },
        /** Whether the list is in a loading state */
        loading: {
            type: Boolean,
            default: false,
        },
        /** Number of skeleton rows to show while loading */
        skeletonCount: {
            type: Number,
            default: 19,
        },
        /** Section label rendered above the list */
        label: {
            type: String,
            default: null,
        },
        /** Custom empty-state message (overrides i18n default) */
        emptyText: {
            type: String,
            default: null,
        },
        /**
         * When true, seats are grouped visually by row and rendered as compact
         * square buttons instead of full-width list rows.
         * Useful for small seat counts or grid-like layouts.
         */
        groupByRow: {
            type: Boolean,
            default: false,
        },
        /**
         * When true, activates seat adjacency enforcement via useAdjacencyRules.
         * Selected seats must be in the same row and form a contiguous block.
         * Disabled by default — no behaviour change for existing consumers.
         */
        enforceAdjacency: {
            type: Boolean,
            default: false,
        },
        /**
         * Controlled-mode: Set of selected seat IDs managed by the parent.
         * When provided, SeatPicker uses this Set to determine selection state
         * instead of its internal state. The parent is the source of truth.
         * Pair with @select to receive toggle events.
         */
        selectedIds: {
            type: Set,
            default: null,
        },
        /**
         * When true, the SeatPicker is in a disabled state and no seat can be toggled.
         * Useful when a selection limit has been reached and the parent wants to
         * block further interaction without clearing the current selection.
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Type of venue for the stage label (e.g. "CANCHA").
         * When null, uses the global venue type from Vuex getters:
         *   tipo===1 → "TERRENO DE JUEGO", otherwise → "ESCENARIO".
         */
        tipoRecinto: {
            type: String,
            default: null,
        },
    });

    // ─── Emits ───────────────────────────────────────────────────────────────

    const emit = defineEmits([
        /**
         * Emitted whenever the selection changes.
         * @payload {Array} selectedSeats — array of full seat objects
         */
        'update:selectedSeats',
        /**
         * Emitted when a seat is toggled (selected or deselected).
         * @payload {{ seat: Object, selected: boolean }}
         */
        'toggle',
        /**
         * Alias of `toggle`. Emitted with the full seat object for controlled-mode
         * consumers that manage selection externally via the `selectedIds` prop.
         * @payload {Object} seat — the full seat object that was clicked
         */
        'select',
        /**
         * Emitted when a toggle attempt is blocked by adjacency rules.
         * Only fired when `enforceAdjacency` is true.
         * @payload {{ seat: Object, reason: string }}
         *   reason: 'different_row' | 'not_adjacent' | 'would_break_contiguity'
         */
        'adjacency-blocked',
    ]);

    // ─── Composable ──────────────────────────────────────────────────────────

    const seatsRef = computed(() => props.seats);

    const {
        selectedSeats,
        selectionCount: internalSelectionCount,
        limitReached: internalLimitReached,
        adjacencyError,
        isSelected: internalIsSelected,
        canToggle: internalCanToggle,
        toggleSeat,
    } = useSeatSelection({
        seats: seatsRef,
        maxSelection: props.maxSelection,
        // Pass the factory fn when adjacency is requested; null otherwise.
        // useSeatSelection calls it internally with the right reactive state.
        adjacencyRules: props.enforceAdjacency ? useAdjacencyRules : null,
    });

    /**
     * Controlled-mode: when `selectedIds` prop is provided by the parent,
     * delegate isSelected checks to it. Otherwise use the internal composable state.
     */
    const isControlled = computed(() => props.selectedIds !== null && props.selectedIds !== undefined);

    function isSelected(seatId) {
        if (isControlled.value) {
            return props.selectedIds.has(String(seatId)) || props.selectedIds.has(seatId);
        }
        return internalIsSelected(seatId);
    }

    /**
     * In controlled mode, limitReached is determined by comparing the external
     * Set size against maxSelection (and also respects the `disabled` prop).
     * In uncontrolled mode, uses the composable's internal limitReached.
     */
    const limitReached = computed(() => {
        if (props.disabled) return true;
        if (isControlled.value) {
            return props.maxSelection > 0 && props.selectedIds.size >= props.maxSelection;
        }
        return internalLimitReached.value;
    });

    function canToggle(seat) {
        if (!seat.available) return false;
        if (props.disabled) return false;
        if (isControlled.value) {
            const selected = isSelected(seat.id);
            if (!selected && limitReached.value) return false;
            return true;
        }
        return internalCanToggle(seat);
    }

    /**
     * In controlled mode, the count comes from the external selectedIds Set size.
     * In uncontrolled mode, use the internal composable counter.
     */
    const selectionCount = computed(() => {
        if (isControlled.value) return props.selectedIds.size;
        return internalSelectionCount.value;
    });

    // ─── Stage (terreno de juego / escenario) ──────────────────────────────

    const manager = computed(() => store.getters.getFooter);

    const stageDisplayLabel = computed(() => {
        if (props.tipoRecinto) return props.tipoRecinto;
        if (manager.value?.tipo === 1) return t('general.terreno_juego');
        return t('general.escenario');
    });

    // ─── Grouped seats (for groupByRow mode) ─────────────────────────────────

    /**
     * Returns seats keyed by row label, preserving the original order.
     * @returns {Object.<string, Array>}
     */
    const seatsByRow = computed(() => {
        const groups = {};
        for (const seat of props.seats) {
            const key = seat.row ?? '?';
            if (!groups[key]) groups[key] = [];
            groups[key].push(seat);
        }
        return groups;
    });

    // ─── Interaction ─────────────────────────────────────────────────────────

    function handleToggle(seat) {
        if (!seat.available) return;
        // Allow deselecting an already-selected seat even when disabled (limit reached).
        // Only block interaction entirely for seats that are NOT currently selected.
        const alreadySelected = isSelected(seat.id);
        if (props.disabled && !alreadySelected) return;

        // Controlled mode: delegate selection management to the parent.
        // Emit `select` with the seat object so the parent can call its own
        // toggle/select logic (e.g. useGenerarInvitaciones.toggleAsiento).
        if (isControlled.value) {
            // Allow deselect always; block new selections only when limit is reached.
            if (!alreadySelected && limitReached.value) return;
            emit('select', seat);
            return;
        }

        const wasSelected = internalIsSelected(seat.id);
        const applied = toggleSeat(seat);
        if (!applied) {
            // If adjacency rules blocked the toggle, surface the reason
            if (props.enforceAdjacency && adjacencyError.value) {
                emit('adjacency-blocked', { seat, reason: adjacencyError.value });
            }
            return;
        }
        emit('toggle', { seat, selected: !wasSelected });
        emit('update:selectedSeats', selectedSeats.value);
    }

    // ─── Visual helpers ───────────────────────────────────────────────────────

    function listItemClasses(seat) {
        if (!seat.available) {
            return 'tw:bg-gray-50 tw:border-gray-200 tw:text-gray-400 tw:cursor-not-allowed tw:opacity-70';
        }
        if (isSelected(seat.id)) {
            return 'tw:bg-blue-50 tw:border-blue-400 tw:text-blue-800 tw:shadow-sm';
        }
        if (limitReached.value) {
            return 'tw:bg-white tw:border-gray-200 tw:text-gray-500 tw:cursor-not-allowed tw:opacity-60';
        }
        return 'tw:bg-white tw:border-gray-200 tw:text-gray-800 tw:hover:bg-gray-50 tw:hover:border-gray-300';
    }

    /**
     * Returns true if the seat represents a gap/aisle placeholder
     * (number is 0 or empty) that should render as invisible spacer.
     */
    function isGap(seat) {
        const num = seat.number ?? seat.numero ?? '';
        return num === 0 || num === '0' || num === '';
    }

    /**
     * Circular dot style — usado en groupByRow mode para imitar el mapa de butacas del diseño Stitch.
     * Verde = disponible, Rojo = ocupado, Azul sólido = seleccionado.
     * flex-1 + aspect-ratio para que los dots llenen el ancho de cada fila proporcionalmente.
     */
    function seatDotClasses(seat) {
        const base = [
            'tw:w-[20px] tw:h-[20px]',
            'tw:rounded-full',
            'tw:flex tw:items-center tw:justify-center',
            'tw:border-0',
            'tw:flex-shrink-0',
        ];
        if (!seat.available) {
            return [...base, 'tw:bg-red-600 tw:text-white tw:cursor-not-allowed'].join(' ');
        }
        if (isSelected(seat.id)) {
            return [...base, 'tw:bg-blue-600 tw:text-white tw:cursor-pointer tw:transition-colors tw:duration-150 tw:select-none'].join(' ');
        }
        if (limitReached.value) {
            return [...base, 'tw:bg-green-400 tw:text-gray-900 tw:cursor-not-allowed tw:opacity-50'].join(' ');
        }
        return [...base, 'tw:bg-green-400 tw:text-gray-900 tw:hover:bg-green-500 tw:hover:scale-125 tw:cursor-pointer tw:transition-colors tw:transition-transform tw:duration-150 tw:select-none'].join(' ');
    }

    function seatTitle(seat) {
        if (!seat.available) return seat.label || `${seat.row}-${seat.number}`;
        if (isSelected(seat.id)) return `${seat.row}-${seat.number} (seleccionado)`;
        return `${seat.row}-${seat.number}`;
    }
</script>
